import React, { useState, Component } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from '../components/Dialog';
import { Palette, Type as TypeIcon, MousePointerClick, TableProperties, Activity, AlertTriangle, LayoutGrid, Map as MapIcon, FormInput, AppWindow, Users, FileText, ShieldAlert, Search, ServerCrash, WifiOff, Clock, Ban } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import { UrgencyBadge } from '../components/UrgencyBadge';
import { KPICard } from '../components/KPICard';
import { SEMUA_STATUS, STATUS_META, statusColor } from '../data/binda';
import { ErrorState } from '../components/ErrorState';
// ============================================================================
// Sistem Desain DENS CAKRA — Panduan Gaya Hidup (Living Style Guide)
// Mendokumentasikan token & komponen bersama yang dipakai seluruh layar.
// ============================================================================
const PALETTE_INTI: {
  name: string;
  varName: string;
}[] = [{
  name: 'Latar',
  varName: '--background'
}, {
  name: 'Permukaan',
  varName: '--card'
}, {
  name: 'Primer',
  varName: '--primary'
}, {
  name: 'Sekunder',
  varName: '--secondary'
}, {
  name: 'Aksen',
  varName: '--accent'
}, {
  name: 'Destruktif',
  varName: '--destructive'
}, {
  name: 'Batas',
  varName: '--border'
}, {
  name: 'Redup',
  varName: '--muted-foreground'
}];
const PALETTE_STATUS: {
  name: string;
  varName: string;
}[] = [{
  name: 'Aktif',
  varName: '--status-active'
}, {
  name: 'Siaga',
  varName: '--status-standby'
}, {
  name: 'Luring',
  varName: '--status-offline'
}, {
  name: 'Darurat',
  varName: '--status-emergency'
}, {
  name: 'Info',
  varName: '--status-info'
}];
function Swatch({
  name,
  varName
}: {
  name: string;
  varName: string;
}) {
  return <div className="flex flex-col gap-2" data-id="element-1011">
      <div className="h-16 rounded border border-border/50" style={{
      backgroundColor: `var(${varName})`
    }} data-id="element-1012" />
      <div data-id="element-1013">
        <p className="text-xs font-medium text-foreground" data-id="element-1014">{name}</p>
        <p className="text-[10px] font-mono text-muted-foreground" data-id="element-1015">{varName}</p>
      </div>
    </div>;
}
function Section({
  icon: Icon,
  title,
  subtitle,
  children
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return <Card className="glass-panel border-border/50" data-id="element-1016">
      <CardHeader className="p-4 border-b border-border/50 bg-card/50" data-id="element-1017">
        <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-1018">
          <Icon className="w-4 h-4 text-primary" data-id="element-1019" /> {title}
        </CardTitle>
        <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mt-1" data-id="element-1020">
          {subtitle}
        </p>
      </CardHeader>
      <CardContent className="p-5" data-id="element-1021">{children}</CardContent>
    </Card>;
}
export default function DesignSystem() {
  const [modalOpen, setModalOpen] = useState(false);
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-1022">
      {/* Header */}
      <div className="flex justify-between items-end border-b border-border/50 pb-4" data-id="element-1023">
        <div data-id="element-1024">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground uppercase flex items-center gap-3" data-id="element-1025">
            <Palette className="w-6 h-6 text-primary text-glow-primary" data-id="element-1026" />
            SISTEM DESAIN
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-1027">
            Pedoman Visual Terpadu // Token & Komponen Bersama
          </p>
        </div>
        <div className="glass-panel px-3 py-1.5 rounded flex items-center gap-2 border-primary/30" data-id="element-1028">
          <span className="relative flex h-2 w-2" data-id="element-1029">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" data-id="element-1030"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" data-id="element-1031"></span>
          </span>
          <span className="text-xs font-mono text-primary tracking-widest" data-id="element-1032">
            DITERAPKAN SEMUA LAYAR
          </span>
        </div>
      </div>

      {/* WARNA */}
      <Section icon={Palette} title="Warna" subtitle="Palet Inti & Token Status Semantik" data-id="element-1033">
        <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-3" data-id="element-1034">
          Palet Inti
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 mb-8" data-id="element-1035">
          {PALETTE_INTI.map(c => <Swatch key={c.varName} {...c} data-id="element-1036" />)}
        </div>
        <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-3" data-id="element-1037">
          Token Status
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4" data-id="element-1038">
          {PALETTE_STATUS.map(c => <Swatch key={c.varName} {...c} data-id="element-1039" />)}
        </div>
      </Section>

      {/* TIPOGRAFI */}
      <Section icon={TypeIcon} title="Tipografi" subtitle="Skala Heading, Teks & Mono + Efek Glow" data-id="element-1040">
        <div className="space-y-4" data-id="element-1041">
          <div className="flex items-baseline gap-4 border-b border-border/30 pb-3" data-id="element-1042">
            <span className="text-[10px] font-mono text-muted-foreground w-32 shrink-0" data-id="element-1043">
              Heading / 2xl
            </span>
            <span className="text-2xl font-heading font-bold tracking-tight text-foreground" data-id="element-1044">
              Pusat Komando Nasional
            </span>
          </div>
          <div className="flex items-baseline gap-4 border-b border-border/30 pb-3" data-id="element-1045">
            <span className="text-[10px] font-mono text-muted-foreground w-32 shrink-0" data-id="element-1046">
              Heading / sm
            </span>
            <span className="text-sm font-heading font-semibold text-foreground" data-id="element-1047">
              Tinjauan Situasi Regional
            </span>
          </div>
          <div className="flex items-baseline gap-4 border-b border-border/30 pb-3" data-id="element-1048">
            <span className="text-[10px] font-mono text-muted-foreground w-32 shrink-0" data-id="element-1049">
              Teks / Tubuh
            </span>
            <span className="text-sm text-foreground/90" data-id="element-1050">
              Laporan intelijen diverifikasi dan dianalisis secara real-time.
            </span>
          </div>
          <div className="flex items-baseline gap-4 border-b border-border/30 pb-3" data-id="element-1051">
            <span className="text-[10px] font-mono text-muted-foreground w-32 shrink-0" data-id="element-1052">
              Mono / Label
            </span>
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1053">
              Sinkronisasi Aktif
            </span>
          </div>
          <div className="flex items-baseline gap-4 flex-wrap" data-id="element-1054">
            <span className="text-[10px] font-mono text-muted-foreground w-32 shrink-0" data-id="element-1055">
              Efek Glow
            </span>
            <span className="text-lg font-mono font-bold text-primary text-glow-primary" data-id="element-1056">
              PRIMER
            </span>
            <span className="text-lg font-mono font-bold text-[var(--status-active)] text-glow-active" data-id="element-1057">
              AKTIF
            </span>
            <span className="text-lg font-mono font-bold text-[var(--status-standby)] text-glow-standby" data-id="element-1058">
              SIAGA
            </span>
            <span className="text-lg font-mono font-bold text-destructive text-glow-destructive" data-id="element-1059">
              DARURAT
            </span>
          </div>
        </div>
      </Section>

      {/* TOMBOL */}
      <Section icon={MousePointerClick} title="Tombol" subtitle="Varian & Ukuran Interaktif" data-id="element-1060">
        <div className="space-y-5" data-id="element-1061">
          <div className="flex flex-wrap items-center gap-3" data-id="element-1062">
            <Button data-id="element-1063">Utama</Button>
            <Button variant="secondary" data-id="element-1064">Sekunder</Button>
            <Button variant="outline" data-id="element-1065">Garis Luar</Button>
            <Button variant="ghost" data-id="element-1066">Hantu</Button>
            <Button variant="destructive" data-id="element-1067">Destruktif</Button>
            <Button variant="link" data-id="element-1068">Tautan</Button>
          </div>
          <div className="flex flex-wrap items-center gap-3" data-id="element-1069">
            <Button size="sm" data-id="element-1070">Kecil</Button>
            <Button data-id="element-1071">Standar</Button>
            <Button size="lg" data-id="element-1072">Besar</Button>
            <Button size="icon" aria-label="Cari" data-id="element-1073">
              <Search className="w-4 h-4" data-id="element-1074" />
            </Button>
            <Button disabled data-id="element-1075">Nonaktif</Button>
          </div>
        </div>
      </Section>

      {/* TABEL */}
      <Section icon={TableProperties} title="Tabel" subtitle="Grid Data Operasional" data-id="element-1076">
        <Table data-id="element-1077">
          <TableHeader className="bg-black/20" data-id="element-1078">
            <TableRow className="border-border/30 hover:bg-transparent" data-id="element-1079">
              <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1080">
                ID
              </TableHead>
              <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1081">
                Wilayah
              </TableHead>
              <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1082">
                Status
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-id="element-1083">
            {[{
            id: 'BINDA-JK',
            wil: 'DKI Jakarta',
            st: 'Normal'
          }, {
            id: 'BINDA-PA',
            wil: 'Papua',
            st: 'Kritis'
          }, {
            id: 'BINDA-JB',
            wil: 'Jawa Barat',
            st: 'Waspada'
          }].map(r => <TableRow key={r.id} className="border-border/20 hover:bg-white/5 transition-colors" data-id="element-1084">
                <TableCell className="font-mono text-xs text-primary" data-id="element-1085">
                  {r.id}
                </TableCell>
                <TableCell className="text-sm font-medium" data-id="element-1086">{r.wil}</TableCell>
                <TableCell data-id="element-1087">
                  <StatusBadge status={r.st} data-id="element-1088" />
                </TableCell>
              </TableRow>)}
          </TableBody>
        </Table>
      </Section>

      {/* INDIKATOR STATUS */}
      <Section icon={Activity} title="Indikator Status" subtitle="Status Wilayah Terpadu" data-id="element-1089">
        <div className="flex flex-wrap gap-3" data-id="element-1090">
          {SEMUA_STATUS.map(s => <StatusBadge key={s} status={s} showDot data-id="element-1091" />)}
        </div>
      </Section>

      {/* LENCANA PERINGATAN */}
      <Section icon={AlertTriangle} title="Lencana Peringatan" subtitle="Tingkat Urgensi & Jenis Insiden" data-id="element-1092">
        <div className="flex flex-wrap gap-3" data-id="element-1093">
          {['Kritis', 'Tinggi', 'Sedang', 'Rendah'].map(u => <UrgencyBadge key={u} urgency={u} data-id="element-1094" />)}
          {['KRITIS', 'WASPADA', 'PERHATIAN', 'INFO'].map(u => <UrgencyBadge key={u} urgency={u} data-id="element-1095" />)}
        </div>
      </Section>

      {/* KARTU KPI */}
      <Section icon={LayoutGrid} title="Kartu KPI" subtitle="Metrik Kunci Operasional" data-id="element-1096">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1097">
          <KPICard title="Personel Aktif" value="12.480" icon={Users} colorVar="var(--primary)" progress={{
          value: 92,
          label: 'Tingkat Penugasan',
          target: '92%'
        }} data-id="element-1098" />
          <KPICard title="Laporan Hari Ini" value="1.248" icon={FileText} colorVar="var(--status-info)" trend={{
          value: '+12.4%',
          label: 'vs kemarin',
          isPositive: true
        }} data-id="element-1099" />
          <KPICard title="Laporan Prioritas" value="7" icon={ShieldAlert} colorVar="var(--destructive)" trend={{
          value: 'TINDAKAN DIPERLUKAN',
          label: '',
          isPositive: false
        }} data-id="element-1100" />
        </div>
      </Section>

      {/* LEGENDA PETA */}
      <Section icon={MapIcon} title="Legenda Peta" subtitle="Kode Warna Status Wilayah pada Peta" data-id="element-1101">
        <div className="glass-panel p-4 rounded border border-border/30 inline-block" data-id="element-1102">
          <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-3" data-id="element-1103">
            Indikator Status
          </p>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2.5" data-id="element-1104">
            {SEMUA_STATUS.map(s => <div key={s} className="flex items-center gap-2.5" data-id="element-1105">
                <span className="inline-flex h-3 w-3 rounded-full shrink-0" style={{
              backgroundColor: statusColor(s),
              boxShadow: `0 0 8px ${statusColor(s)}`
            }} data-id="element-1106" />
                <span className="text-xs font-mono uppercase tracking-wider text-foreground/90" data-id="element-1107">
                  {STATUS_META[s].label}
                </span>
              </div>)}
          </div>
        </div>
      </Section>

      {/* ELEMEN FORMULIR */}
      <Section icon={FormInput} title="Elemen Formulir" subtitle="Input, Pilihan & Area Teks Taktis" data-id="element-1108">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl" data-id="element-1109">
          <div data-id="element-1110">
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1111">
              Input Teks
            </label>
            <input type="text" placeholder="Masukkan kata kunci..." className="w-full mt-1.5 bg-background/50 border border-border/50 rounded p-2.5 text-xs font-mono focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all placeholder:text-muted-foreground/50" data-id="element-1112" />
          </div>
          <div data-id="element-1113">
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1114">
              Pilihan
            </label>
            <select className="w-full mt-1.5 bg-background/50 border border-border/50 rounded p-2.5 text-xs font-mono focus:outline-none focus:border-primary/50 transition-all" data-id="element-1115">
              <option data-id="element-1116">Keamanan</option>
              <option data-id="element-1117">Sosial</option>
              <option data-id="element-1118">Siber</option>
            </select>
          </div>
          <div className="md:col-span-2" data-id="element-1119">
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1120">
              Area Teks
            </label>
            <textarea placeholder="Masukkan detail observasi..." className="w-full mt-1.5 bg-background/50 border border-border/50 rounded p-2.5 text-xs font-mono min-h-[90px] focus:outline-none focus:border-primary/50 transition-all placeholder:text-muted-foreground/50" data-id="element-1121" />
          </div>
          <div className="md:col-span-2" data-id="element-1122">
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1123">
              Nonaktif
            </label>
            <input type="text" disabled value="Bidang terkunci" className="w-full mt-1.5 bg-background/30 border border-border/30 rounded p-2.5 text-xs font-mono text-muted-foreground/60 cursor-not-allowed" data-id="element-1124" />
          </div>
        </div>
      </Section>

      {/* JENDELA MODAL */}
      <Section icon={AppWindow} title="Jendela Modal" subtitle="Dialog Konfirmasi & Detail" data-id="element-1125">
        <Dialog open={modalOpen} onOpenChange={setModalOpen} data-id="element-1126">
          <DialogTrigger asChild data-id="element-1127">
            <Button variant="outline" data-id="element-1128">
              <AppWindow className="w-4 h-4 mr-2" data-id="element-1129" /> Buka Jendela Modal
            </Button>
          </DialogTrigger>
          <DialogContent className="glass-panel-heavy border-border/50" data-id="element-1130">
            <DialogHeader data-id="element-1131">
              <DialogTitle className="font-mono uppercase tracking-widest text-sm flex items-center gap-2" data-id="element-1132">
                <ShieldAlert className="w-4 h-4 text-destructive" data-id="element-1133" /> Konfirmasi
                Eskalasi
              </DialogTitle>
              <DialogDescription className="font-mono text-xs text-muted-foreground" data-id="element-1134">
                Tindakan ini akan meneruskan laporan prioritas ke Markas Besar
                Nasional. Lanjutkan?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter data-id="element-1135">
              <Button variant="outline" onClick={() => setModalOpen(false)} data-id="element-1136">
                Batal
              </Button>
              <Button variant="destructive" onClick={() => setModalOpen(false)} data-id="element-1137">
                Eskalasi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Section>

      {/* STATUS GALAT (ERROR STATES) */}
      <Section icon={ServerCrash} title="Status Galat" subtitle="Penanganan Kesalahan Sistem & Jaringan" data-id="element-1138">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1139">
          <ErrorState icon={WifiOff} title="KONEKSI TERPUTUS" description="Sambungan ke peladen pusat terputus. Pastikan perangkat Anda terhubung ke jaringan aman." actionLabel="Coba Ulang" onAction={() => console.log('Retrying connection...')} variant="warning" data-id="element-1140" />
          <ErrorState icon={Ban} title="AKSES DITOLAK" description="Tingkat izin Anda tidak mencukupi untuk melihat data ini. Hubungi administrator sistem." actionLabel="Kembali" onAction={() => console.log('Going back...')} variant="destructive" data-id="element-1141" />
          <ErrorState icon={Clock} title="SESI BERAKHIR" description="Sesi otentikasi Anda telah kedaluwarsa demi keamanan. Silakan masuk kembali." actionLabel="Masuk Ulang" onAction={() => console.log('Redirecting to login...')} variant="warning" data-id="element-1142" />
          <ErrorState icon={ServerCrash} title="SERVER TIDAK TERSEDIA" description="Pusat data sedang mengalami gangguan atau pemeliharaan. Tim teknis sedang menangani masalah ini." actionLabel="Cek Status" onAction={() => console.log('Checking system status...')} variant="destructive" data-id="element-1143" />
        </div>
      </Section>
    </div>;
}